import type { ComputedRef } from "vue";
import { computed } from "vue";
import { useRoute } from "vue-router";

export interface SortFieldDescriptor {
  /** Algolia virtual-replica segment (e.g. `first_name`). */
  indexPart: string;
  /** Display label shown in the sort dropdown. */
  label: string;
  /**
   * PrimeVue DataTable column field path the sort applies to. Used so the table header arrow
   * highlights the correct column. Will be undefined for sort fields with no matching column
   * (none today, but the optionality leaves room for future Algolia-only sort dimensions).
   */
  columnField?: string;
}

export type SortOrder = "asc" | "desc";

export interface SortItem {
  /** Full virtual-replica index name passed to <ais-sort-by> / refine(). */
  value: string;
  /** Display label (field name only — direction is rendered separately by the icon button). */
  label: string;
  field: SortFieldDescriptor;
  order: SortOrder;
}

interface UseMyStudentsAlgoliaIndex {
  defaultIndexName: ComputedRef<string>;
  sortItems: ComputedRef<SortItem[]>;
  sortUrlToIndex: (urlValue?: string) => string | undefined;
  sortIndexToUrl: (fullIndex?: string) => string | undefined;
  indexBase: ComputedRef<string>;
  indexPrefix: ComputedRef<string>;
  pageIndex: ComputedRef<string>;
  hasGroupedOrders: ComputedRef<boolean>;
  sortOrder: ComputedRef<SortOrder>;
  selectedSortField: ComputedRef<SortFieldDescriptor>;
  currentSort: ComputedRef<SortItem>;
}

export const SortField = Object.freeze({
  FIRST_NAME: { indexPart: "first_name", label: "First Name", columnField: "student.first_name_legal" },
  LAST_NAME: { indexPart: "last_name", label: "Last Name", columnField: "student.last_name" },
  EMAIL: { indexPart: "email", label: "Email", columnField: "student.email" },
  LAST_ACTIVE: { indexPart: "created_at", label: "Last Active", columnField: "student.created_at" },
}) satisfies Record<string, SortFieldDescriptor>;

const INDEX_SORT_ASC: SortOrder = "asc";
const INDEX_SORT_DESC: SortOrder = "desc";
// Default chosen to preserve prior behavior — initial index used by <ais-instant-search>
// when no ?sort URL param is present.
const DEFAULT_SORT_FIELD: SortFieldDescriptor = SortField.LAST_NAME;
const DEFAULT_SORT_ORDER: SortOrder = INDEX_SORT_ASC;
const DEFAULT_SORT_URL_VALUE = `${DEFAULT_SORT_FIELD.indexPart}_${DEFAULT_SORT_ORDER}`;

const MY_STUDENT_ORDERS_INDEX = "student_orders";

const indexPrefix: ComputedRef<string> = computed(() => {
  const host = window.location.host;
  const firstHostPiece = host.split(".")[0];
  if (firstHostPiece.indexOf("test") !== -1) return firstHostPiece;
  return import.meta.env.VITE_APP_ENV === "production" ? "production" : "staging";
});

export const useMyStudentsAlgoliaIndex = (): UseMyStudentsAlgoliaIndex => {
  const route = useRoute();

  const pageIndex = computed<string>(() => {
    switch (route?.params.page) {
      default:
        return MY_STUDENT_ORDERS_INDEX;
    }
  });

  const hasGroupedOrders = computed<boolean>(() => false);

  // Base index (no sort segments). All sort variants are virtual replicas of this.
  const indexBase = computed<string>(() => `${indexPrefix.value}_${pageIndex.value}`);

  // Initial index passed to <ais-instant-search>. <ais-sort-by> swaps to other items via uiState.sortBy.
  const defaultIndexName = computed<string>(() => `${indexBase.value}_${DEFAULT_SORT_URL_VALUE}`);

  // Cartesian (field × order) consumed by <ais-sort-by :items="sortItems">. Each value is the full
  // virtual-replica index name; field/order are kept for the UI's split label + direction controls.
  const sortItems = computed<SortItem[]>(() =>
    Object.values(SortField).flatMap((field) =>
      ([INDEX_SORT_ASC, INDEX_SORT_DESC] as const).map<SortItem>((order) => ({
        value: `${indexBase.value}_${field.indexPart}_${order}`,
        label: field.label,
        field,
        order,
      })),
    ),
  );

  // Converters for useAlgoliaQueryRouting to (de)serialize sortBy ⇄ ?sort URL param.
  // Strips the env+pageIndex prefix so URLs stay clean (?sort=first_name_asc rather than
  // ?sort=staging_student_orders_first_name_asc).
  const sortUrlToIndex = (urlValue?: string): string | undefined => {
    if (!urlValue) return undefined;
    return `${indexBase.value}_${urlValue}`;
  };
  const sortIndexToUrl = (fullIndex?: string): string | undefined => {
    if (!fullIndex || fullIndex === defaultIndexName.value) return undefined;
    const prefix = `${indexBase.value}_`;
    return fullIndex.startsWith(prefix) ? fullIndex.slice(prefix.length) : undefined;
  };

  // Current sort derived from the URL. List components use these to drive PrimeVue DataTable header arrows.
  const currentSort = computed<SortItem>(() => {
    const rawSort = route?.query?.sort;
    const urlValue = typeof rawSort === "string" ? rawSort : DEFAULT_SORT_URL_VALUE;
    const fullIndex = sortUrlToIndex(urlValue);
    return sortItems.value.find((item) => item.value === fullIndex) ?? sortItems.value[0];
  });

  const selectedSortField = computed<SortFieldDescriptor>(() => currentSort.value.field);
  const sortOrder = computed<SortOrder>(() => currentSort.value.order);

  return {
    defaultIndexName,
    sortItems,
    sortUrlToIndex,
    sortIndexToUrl,
    indexBase,
    indexPrefix,
    pageIndex,
    hasGroupedOrders,
    sortOrder,
    selectedSortField,
    currentSort,
  };
};
